import React, { useState, useEffect, useCallback } from 'react';
import {
  Megaphone,
  Plus,
  Edit,
  Trash2,
  Loader2,
  Info,
  AlertTriangle,
  Gift,
  RefreshCw,
  Star,
} from 'lucide-react';
import { useTheme } from '@framework/contexts/ThemeContext';
import BaseModal from '@framework/components/BaseModal';
import ActionButton from '@framework/components/ActionButton';
import ErrorAlert from '@framework/components/ErrorAlert';
import { parseApiDate } from '@framework/utils/dateUtils';

const ANNOUNCEMENT_TYPES = [
  { value: 'info', label: 'Info', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' },
  { value: 'promo', label: 'Promo', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' },
  { value: 'update', label: 'Update', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' },
  { value: 'warning', label: 'Warning', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' },
  { value: 'rate', label: 'Rate App', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' },
];

const CTA_ACTIONS = [
  { value: 'none', label: 'None' },
  { value: 'url', label: 'Open URL' },
  { value: 'paywall', label: 'Show Paywall' },
  { value: 'chat', label: 'Open Chat' },
  { value: 'recipe', label: 'Open Recipe' },
  { value: 'rate', label: 'Rate the App' },
];

const TIERS = ['free', 'plus', 'pro'];

const EMPTY_FORM = {
  title: '',
  body: '',
  image_url: '',
  cta_label: '',
  cta_action: 'none',
  cta_value: '',
  type: 'info',
  target_tiers: [],
  target_user_ids: '', // comma-separated ids in the form; parsed on save
  starts_at: '',
  ends_at: '',
  is_active: true,
  is_dismissible: true,
  dismiss_days: 7,
  priority: 0,
};

function getTypeBadge(type) {
  const t = ANNOUNCEMENT_TYPES.find((at) => at.value === type);
  if (!t) return null;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${t.color}`}>
      {t.label}
    </span>
  );
}

function computeStatus(ann) {
  const now = new Date();
  if (ann.ends_at && parseApiDate(ann.ends_at) <= now) return 'expired';
  if (!ann.is_active) return 'inactive';
  if (ann.starts_at && parseApiDate(ann.starts_at) > now) return 'scheduled';
  return 'active';
}

const STATUS_STYLES = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  expired: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  inactive: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
};

function getStatusBadge(ann) {
  const status = computeStatus(ann);
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status]}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function formatDatetime(isoString) {
  const d = parseApiDate(isoString);
  if (!d) return '--';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function toLocalDatetimeValue(isoString) {
  const d = parseApiDate(isoString);
  if (!d) return '';
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
}

function fromLocalDatetimeValue(localValue) {
  if (!localValue) return '';
  return new Date(localValue).toISOString();
}

// ---------------------------------------------------------------------------
// Live preview — mirrors Flutter AnnouncementCard exactly
// ---------------------------------------------------------------------------

function resolvePreviewStyles(type) {
  // Returns: { bg, textColor, ctaBg, ctaFg, gradient }
  // Matches _resolveColors() in announcement_card.dart
  switch (type) {
    case 'promo':
      return {
        gradient: 'linear-gradient(135deg, #FF5722, #9C27B0)', // deepOrange → purple
        textColor: '#FFFFFF',
        ctaBg: '#FFFFFF',
        ctaFg: '#FF5722',
      };
    case 'rate':
      return {
        gradient: 'linear-gradient(135deg, #FFB300, #FF8F00)', // amber gradient
        textColor: '#FFFFFF',
        ctaBg: '#FFFFFF',
        ctaFg: '#FF8F00',
      };
    case 'warning':
      return {
        bg: '#FFFDE7', // amber.shade50
        textColor: '#F57F17', // amber.shade800
        ctaBg: '#F9A825', // amber.shade600
        ctaFg: '#FFFFFF',
      };
    case 'update':
      return {
        bg: '#E8F5E9', // green.shade50
        textColor: '#2E7D32', // green.shade800
        ctaBg: '#43A047', // green.shade600
        ctaFg: '#FFFFFF',
      };
    case 'info':
    default:
      return {
        bg: '#E3F2FD', // blue.shade50
        textColor: '#1565C0', // blue.shade800
        ctaBg: '#1E88E5', // blue.shade600
        ctaFg: '#FFFFFF',
      };
  }
}

function AnnouncementPreview({ form }) {
  const styles = resolvePreviewStyles(form.type);
  const hasGradient = form.type === 'promo' || form.type === 'rate';
  const hasTitle = form.title.trim();
  const hasBody = form.body?.trim();
  const hasImage = form.image_url?.trim();
  const hasCta = form.cta_label?.trim();

  if (!hasTitle && !hasBody) {
    return (
      <div className="text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
        Start typing to see the preview
      </div>
    );
  }

  return (
    <div
      className="relative rounded-xl shadow-md"
      style={{
        background: styles.gradient || styles.bg,
        padding: '16px',
      }}
    >
      {/* Title */}
      {hasTitle && (
        <div
          style={{
            fontWeight: 700,
            fontSize: '15px',
            color: styles.textColor,
            paddingRight: form.is_dismissible ? '28px' : '0',
          }}
        >
          {form.title}
        </div>
      )}

      {/* Body */}
      {hasBody && (
        <div
          style={{
            fontSize: '13px',
            color: hasGradient ? 'rgba(255,255,255,0.9)' : styles.textColor,
            opacity: hasGradient ? 1 : 0.85,
            marginTop: '6px',
          }}
        >
          {form.body}
        </div>
      )}

      {/* Image */}
      {hasImage && (
        <div className="mt-2.5 rounded-lg overflow-hidden" style={{ height: '140px' }}>
          <img
            src={form.image_url}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}

      {/* CTA Button */}
      {hasCta && (
        <button
          type="button"
          className="w-full mt-3 rounded-lg font-semibold text-sm py-2.5 cursor-default"
          style={{
            backgroundColor: styles.ctaBg,
            color: styles.ctaFg,
          }}
        >
          {form.cta_label}
        </button>
      )}

      {/* Dismiss X */}
      {form.is_dismissible && (
        <div
          className="absolute top-2 right-2 flex items-center justify-center rounded-full"
          style={{
            width: '24px',
            height: '24px',
            backgroundColor: hasGradient ? 'rgba(255,255,255,0.25)' : `${styles.textColor}15`,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={hasGradient ? '#FFFFFF' : styles.textColor} strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AnnouncementsPage() {
  const { apiService: api } = useTheme();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // ---- Data loading ----

  const loadAnnouncements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get('/announcements');
      setAnnouncements(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load announcements:', err);
      setError('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  // ---- Modal helpers ----

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (ann) => {
    setEditingId(ann.id);
    setForm({
      title: ann.title || '',
      body: ann.body || '',
      image_url: ann.image_url || '',
      cta_label: ann.cta_label || '',
      cta_action: ann.cta_action || 'none',
      cta_value: ann.cta_value || '',
      type: ann.type || 'info',
      target_tiers: ann.target_tiers || [],
      target_user_ids: (ann.target_user_ids || []).join(', '),
      starts_at: toLocalDatetimeValue(ann.starts_at),
      ends_at: toLocalDatetimeValue(ann.ends_at),
      is_active: ann.is_active ?? true,
      is_dismissible: ann.is_dismissible ?? true,
      dismiss_days: ann.dismiss_days ?? 7,
      priority: ann.priority ?? 0,
    });
    setFormError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
    setFormError(null);
  };

  // ---- Form field change ----

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleTier = (tier) => {
    setForm((prev) => {
      const tiers = prev.target_tiers.includes(tier)
        ? prev.target_tiers.filter((t) => t !== tier)
        : [...prev.target_tiers, tier];
      return { ...prev, target_tiers: tiers };
    });
  };

  // ---- Save ----

  const handleSave = async () => {
    if (!form.title.trim()) {
      setFormError('Title is required');
      return;
    }

    // The app only renders the CTA button when a label exists — an action
    // without a label would be silently dead in the card.
    if (form.cta_action !== 'none' && !form.cta_label.trim()) {
      setFormError('Button label is required when a CTA action is selected');
      return;
    }

    const userIds = form.target_user_ids
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map(Number);
    if (userIds.some((n) => !Number.isInteger(n) || n <= 0)) {
      setFormError('Target user IDs must be positive numbers, comma-separated');
      return;
    }

    setSaving(true);
    setFormError(null);

    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        target_user_ids: userIds.length > 0 ? userIds : null,
        body: form.body.trim() || null,
        image_url: form.image_url.trim() || null,
        cta_label: form.cta_label.trim() || null,
        cta_action: form.cta_action === 'none' ? null : form.cta_action,
        cta_value: !['none', 'rate'].includes(form.cta_action) ? (form.cta_value.trim() || null) : null,
        target_tiers: form.target_tiers.length > 0 ? form.target_tiers : null,
        starts_at: form.starts_at ? fromLocalDatetimeValue(form.starts_at) : null,
        ends_at: form.ends_at ? fromLocalDatetimeValue(form.ends_at) : null,
        dismiss_days: form.is_dismissible ? Number(form.dismiss_days) : null,
        priority: Number(form.priority),
      };

      if (editingId) {
        await api.put(`/announcements/${editingId}`, payload);
      } else {
        await api.post('/announcements', payload);
      }

      closeModal();
      await loadAnnouncements();
    } catch (err) {
      console.error('Failed to save announcement:', err);
      setFormError(err?.response?.data?.detail || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // ---- Delete ----

  const handleDelete = async (ann) => {
    if (!confirm(`Delete announcement "${ann.title}"?`)) return;
    try {
      await api.delete(`/announcements/${ann.id}`);
      await loadAnnouncements();
    } catch (err) {
      console.error('Failed to delete announcement:', err);
      setError('Failed to delete announcement');
    }
  };

  // ---- Render helpers ----

  const renderTiers = (tiers) => {
    if (!tiers || tiers.length === 0) {
      return <span className="text-gray-400 dark:text-gray-500 text-xs">All tiers</span>;
    }
    return tiers.map((t) => (
      <span
        key={t}
        className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 mr-1"
      >
        {t}
      </span>
    ));
  };

  // ---- Loading state ----

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // ---- Main render ----

  return (
    <div className="max-w-6xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Megaphone className="w-6 h-6 text-orange-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Announcements</h1>
          <span className="text-sm text-gray-500 dark:text-gray-400">({announcements.length})</span>
        </div>
        <div className="flex items-center space-x-2">
          <ActionButton icon={RefreshCw} variant="ghost" size="sm" onClick={loadAnnouncements}>
            Refresh
          </ActionButton>
          <ActionButton icon={Plus} variant="primary" size="sm" onClick={openCreate}>
            New Announcement
          </ActionButton>
        </div>
      </div>

      <ErrorAlert error={error} onClose={() => setError(null)} />

      {/* Table */}
      {announcements.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
          <Megaphone className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">No announcements yet.</p>
          <ActionButton icon={Plus} variant="primary" size="sm" className="mt-4" onClick={openCreate}>
            Create your first announcement
          </ActionButton>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tiers</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date Range</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {announcements.map((ann) => (
                  <tr key={ann.id} className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs" title={ann.title}>
                        {ann.title}
                      </div>
                      {ann.body && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs" title={ann.body}>
                          {ann.body}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">{getTypeBadge(ann.type)}</td>
                    <td className="px-4 py-3">{getStatusBadge(ann)}</td>
                    <td className="px-4 py-3">
                      {renderTiers(ann.target_tiers)}
                      {ann.target_user_ids?.length > 0 && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300">
                          users: {ann.target_user_ids.join(', ')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatDatetime(ann.starts_at)} &mdash; {formatDatetime(ann.ends_at)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{ann.priority}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-1">
                        <ActionButton icon={Edit} variant="ghost" size="xs" onClick={() => openEdit(ann)} title="Edit" />
                        <ActionButton icon={Trash2} variant="ghost" size="xs" onClick={() => handleDelete(ann)} title="Delete" className="text-red-500 hover:text-red-700" />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      <BaseModal isOpen={modalOpen} onClose={closeModal} title={editingId ? 'Edit Announcement' : 'New Announcement'} size="xl">
        <div className="p-6 flex gap-6">
          {/* Left: Form fields */}
          <div className="flex-1 min-w-0 space-y-5">
          <ErrorAlert error={formError} onClose={() => setFormError(null)} />

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Announcement title"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Body</label>
            <textarea
              value={form.body}
              onChange={(e) => updateField('body', e.target.value)}
              rows={3}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-y"
              placeholder="Optional body text"
            />
          </div>

          {/* Image URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Image URL</label>
            <input
              type="text"
              value={form.image_url}
              onChange={(e) => updateField('image_url', e.target.value)}
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="https://..."
            />
          </div>

          {/* CTA row */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CTA Label</label>
              <input
                type="text"
                value={form.cta_label}
                onChange={(e) => updateField('cta_label', e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="e.g. Learn More"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CTA Action</label>
              <select
                value={form.cta_action}
                onChange={(e) => updateField('cta_action', e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                {CTA_ACTIONS.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </div>
            {!['none', 'rate'].includes(form.cta_action) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CTA Value</label>
                <input
                  type="text"
                  value={form.cta_value}
                  onChange={(e) => updateField('cta_value', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder={form.cta_action === 'url' ? 'https://...' : 'value'}
                />
              </div>
            )}
          </div>

          {/* Type + Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
              <select
                value={form.type}
                onChange={(e) => updateField('type', e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                {ANNOUNCEMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
              <input
                type="number"
                value={form.priority}
                onChange={(e) => updateField('priority', e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Target Tiers */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Tiers</label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Leave all unchecked to target all tiers.
            </p>
            <div className="flex items-center space-x-4">
              {TIERS.map((tier) => (
                <label key={tier} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.target_tiers.includes(tier)}
                    onChange={() => toggleTier(tier)}
                    className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{tier}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Target Users */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Target Users</label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Comma-separated user IDs to show this to specific users only. Leave empty for everyone.
            </p>
            <input
              type="text"
              value={form.target_user_ids}
              onChange={(e) => updateField('target_user_ids', e.target.value)}
              placeholder="e.g. 251, 255"
              className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          {/* Date range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Starts At</label>
              <input
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => updateField('starts_at', e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ends At</label>
              <input
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => updateField('ends_at', e.target.value)}
                className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Toggles row */}
          <div className="flex flex-wrap items-center gap-6">
            {/* is_active */}
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => updateField('is_active', e.target.checked)}
                className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
            </label>

            {/* is_dismissible */}
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_dismissible}
                onChange={(e) => updateField('is_dismissible', e.target.checked)}
                className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Dismissible</span>
            </label>

            {/* dismiss_days (conditional) */}
            {form.is_dismissible && (
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">Dismiss for</label>
                <input
                  type="number"
                  min="1"
                  value={form.dismiss_days}
                  onChange={(e) => updateField('dismiss_days', e.target.value)}
                  className="w-20 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <span className="text-sm text-gray-500 dark:text-gray-400">days</span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <ActionButton variant="secondary" size="md" onClick={closeModal} disabled={saving}>
              Cancel
            </ActionButton>
            <ActionButton variant="primary" size="md" onClick={handleSave} loading={saving}>
              {editingId ? 'Save Changes' : 'Create'}
            </ActionButton>
          </div>
          </div>

          {/* Right: Live phone preview */}
          <div className="w-72 flex-shrink-0">
            <div className="sticky top-0">
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">App Preview</p>
              {/* Phone frame */}
              <div className="bg-gray-100 dark:bg-gray-900 rounded-[2rem] p-3 shadow-inner border border-gray-200 dark:border-gray-700">
                {/* Notch */}
                <div className="flex justify-center mb-2">
                  <div className="w-20 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
                </div>
                {/* Screen area */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 min-h-[280px]">
                  {/* Mock header (FooZenie) */}
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                      <span className="text-sm">🧞</span>
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-900 dark:text-white">FooZenie</div>
                      <div className="text-[10px] text-gray-400">What shall we cook today?</div>
                    </div>
                  </div>
                  {/* Mock stats row */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-full px-2 py-1">
                      <span className="text-[9px] text-gray-500">🍽 12 recipes</span>
                      <span className="text-gray-300 dark:text-gray-600">|</span>
                      <span className="text-[9px] text-gray-500">❤️ 3</span>
                    </div>
                    <div className="bg-orange-50 dark:bg-orange-900/20 rounded-full px-2 py-1">
                      <span className="text-[9px] text-orange-500 font-medium">🎲 Surprise Me</span>
                    </div>
                  </div>
                  {/* Announcement preview */}
                  <AnnouncementPreview form={form} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </BaseModal>
    </div>
  );
}
